import React, { useContext, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAlert } from "react-alert";

import { SettingsContext } from "../contexts";
import RCListingForm from "../forms/RCListingForm.jsx";
import { PdfShower } from "../components";
import {
  FlowSection,
  AssignPersonPicker,
  SellRequestSearch,
  LinkedRequestSlot,
  IntakeFileBox,
  AiStatus,
  RepairToggleField,
  OwnedByField,
  IUserIcon,
} from "./FlowKit.jsx";
import ContractsSection from "./ContractsSection.jsx";
import { Button, ButtonGroup, Radio, RadioGroup } from "../components/ui";
import ListingStatusSection from "./ListingStatusSection.jsx";
import useAutoContractDocs from "./useAutoContractDocs";
import {
  uploadIntakeFile,
  analyzeIntakeDocuments,
  resolveSuggestedProduct,
  fetchRequest,
  fetchPurchasingAgreement,
  fetchConsignmentAgreement,
  listingPrefillFromRequest,
  createDoneSellRequest,
  attachListingToRequest,
  sellRequestActionOnSave,
  activateListing,
  continueTargetAfterIntake,
} from "./intakeApi";

const personName = (u) =>
  [u?.first_name, u?.last_name].filter(Boolean).join(" ") || u?.email || (u?.id ? `User #${u.id}` : "");

// WALK-IN CLIENT and the old NEW SELL REQUEST were merged 2026-08-27: the
// second option rendered no panel of its own — same consignor picker, no unique
// input — and differed only in creating the linked Sell Request on save, which
// a walk-in wants too. One option, one panel, and the pictures inside it are
// optional (a rep with the paperwork in hand needs neither).
const OPTIONS = [
  { key: "walkin",   label: "Walk-in Client",   hint: "Pick or create the consignor; their Sell Request is created on save. Pictures optional." },
  { key: "existing", label: "Existing Request", hint: "Reuse a Sell Request already in the store." },
];

/**
 * NEW CONSIGNMENT — purpose-built create flow.
 *
 * ASSIGN CONSIGNOR offers exactly one of two intake routes:
 *   1. Walk-in Client — pick or create the consignor, optionally uploading
 *      watch + warranty-card photos for Gemini to identify the product and read
 *      serial number / warranty date. On save a linked Sell Request is created
 *      in terminal ('done') status.
 *   2. Existing Request — store-wide Sell Request search; its client, product
 *      and generated documents (PO / purchasing / consignment agreements)
 *      carry over, and the chosen request is linked to the listing on save.
 *
 * The listing is stamped internal_product='exclusive' (Consignment) — the same
 * field the listing card's Consignment badge and toggle already read.
 */
const RCConsignmentListingCreate = () => {
  const history = useHistory();
  const alert = useAlert();
  const settingsCtx = useContext(SettingsContext);
  const iframeQueryParams = settingsCtx?.iframeQueryParams ?? settingsCtx?.settings?.iframeQueryParams ?? "";

  // ── intake state ────────────────────────────────────────────────────────────
  const [openSection, setOpenSection] = useState("consignor");
  const [option, setOption] = useState(null);                   // walkin | existing
  const [watchImage, setWatchImage] = useState(null);           // {id, name}
  const [warrantyImage, setWarrantyImage] = useState(null);     // {id, name}
  const [uploadingSlot, setUploadingSlot] = useState(null);     // 'watch' | 'warranty'
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [aiState, setAiState] = useState(null);
  const [aiFields, setAiFields] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  // A read that succeeded but produced nothing to show. Measured on the two
  // OUTPUTS the rep can see — the suggestion chips and the prefilled fields —
  // rather than on the product, which they may have picked themselves.
  const aiEmpty = !suggestions.length && !aiFields;
  const [consignor, setConsignor] = useState(null);
  // The product in play: auto-picked from the images / selected request, OR the
  // one the rep chose in the form's own selector (reported back by the form).
  const [product, setProduct] = useState(null);
  const [agreements, setAgreements] = useState({});             // {purchasing: {...}, consignment: {...}}
  // A request created mid-flow (or an existing one selected) exists before the
  // listing does, so its link is made once the listing has an id. Holds that
  // request's id until then.
  const [pendingAttachId, setPendingAttachId] = useState(null);
  // The request THIS flow raised, if any. One listing raises at most one, and
  // `pendingAttachId` cannot carry that rule on its own: unlinking clears it,
  // and the save path would then create a SECOND request for a consignor who
  // already has one. This latches instead — never cleared once set.
  const [createdRequestId, setCreatedRequestId] = useState(null);
  const intentRef = useRef("draft");
  // "In Repair" — page state shared by the ASSIGN CONSIGNOR toggle (below)
  // and the Cost-tab toggle (via enforce + reportFields), replacing the old
  // CREATE REPAIR button.
  const [isRepair, setIsRepair] = useState(false);
  // "Owned By" — same arrangement, one field shared by the ASSIGN CONSIGNOR
  // dropdown (below) and the Listings-section dropdown inside Formik. Seeded to
  // "exclusive", which is what makes this listing a consignment. Never a second
  // source of truth — see `enforce` / `reportFields` below.
  const [ownedBy, setOwnedBy] = useState("exclusive");

  const toggleSection = (key) => setOpenSection((s) => (s === key ? null : key));

  // Exactly one of the two options — switching resets the other route's state.
  // The link falls back to a request THIS flow raised: that one belongs to the
  // listing whichever route is on screen, where a merely-selected request was
  // part of the route being abandoned.
  const pickOption = (key) => {
    if (key === option) return;
    setOption(key);
    setWatchImage(null);
    setWarrantyImage(null);
    setSelectedRequest(null);
    setPendingAttachId(createdRequestId);
    setAiState(null);
    setAiFields(null);
    setSuggestions([]);
    setAgreements({});
  };

  // Hand over to the FORM's accordion (a separate one from the intake section).
  const [formSectionRequest, setFormSectionRequest] = useState(null);
  const openFormSection = (key) =>
    setFormSectionRequest((prev) => ({ key, nonce: (prev?.nonce ?? 0) + 1 }));

  // ASSIGN CONSIGNOR is committed by an explicit Continue — the section's last
  // field is the In Repair toggle, so nothing above it may collapse on its own.
  const intakeSatisfied = option === "existing" ? !!selectedRequest : !!consignor?.id;

  // Where Continue lands depends on what is actually known. A walk-in whose
  // pictures were read (or an existing request) already has a product, so the
  // rep can go straight to LISTINGS. A consignor picked with no pictures does
  // NOT — jumping to LISTINGS there silently skips SELECT PRODUCT and leaves the
  // listing with no product at all, so open the product picker instead.
  const [productRequest, setProductRequest] = useState(null);
  const requestProductPicker = () =>
    setProductRequest((prev) => ({ nonce: (prev?.nonce ?? 0) + 1 }));

  const continueFromConsignor = () => {
    setOpenSection(null);
    if (continueTargetAfterIntake({ product }) === "listings") {
      openFormSection("listings");
      return;
    }
    requestProductPicker();
  };

  // ── Option 1: walk-in images ────────────────────────────────────────────────
  const analyzeImages = async (ids) => {
    setAiState("loading");
    const result = await analyzeIntakeDocuments("watch_images", ids);
    if (!result) {
      setAiState("unavailable");
      setAiFields(null);
      return;
    }
    setAiState("done");
    setSuggestions(result.users || []);
    setAiFields(result.fields || null);
    const matched = await resolveSuggestedProduct(result.product);
    if (matched) setProduct(matched);
  };

  const onImagePick = async (slot, file) => {
    setUploadingSlot(slot);
    try {
      const uploaded = await uploadIntakeFile(file);
      const nextWatch = slot === "watch" ? uploaded : watchImage;
      const nextWarranty = slot === "warranty" ? uploaded : warrantyImage;
      if (slot === "watch") setWatchImage(uploaded);
      else setWarrantyImage(uploaded);
      await analyzeImages([nextWatch?.id, nextWarranty?.id].filter(Boolean));
    } catch (e) {
      alert.error("Image upload failed — please try again.");
    } finally {
      setUploadingSlot(null);
    }
  };

  // ── Option 2: existing request ──────────────────────────────────────────────
  // Selecting a request ARMS ITS LINK. It used to only fill the fields, so the
  // saved listing was never pointed at the request the rep had chosen —
  // attachListingToRequest ran for a request created in-flow and nothing else.
  const onRequestSelect = async (req) => {
    setPendingAttachId(req.id);
    try {
      const full = await fetchRequest(req.id);
      setSelectedRequest(full);
      if (full.client?.id) {
        setConsignor({ id: full.client.id, name: personName(full.client) });
        setSuggestions([full.client]);
      }
      if (full.product_id || full.product?.id) {
        const matched = await resolveSuggestedProduct({ id: full.product_id || full.product?.id });
        if (matched) setProduct(matched);
      }
    } catch (e) {
      setSelectedRequest(req);
      alert.info("Loaded the request summary only — details could not be fetched.");
    }
  };

  const poUrl =
    selectedRequest?.xero_purchase_order_file_url ??
    selectedRequest?.xero_purchase_order_file_id ??
    null;

  // ── Create the consignor's Sell Request NOW ────────────────────────────────
  // Clicking "Create Sell Request" creates it and shows it as linked, rather
  // than arming a flag the save handler acts on later. The listing does not
  // exist yet, so the link is deferred to afterSubmit — that part cannot be
  // helped, but the request itself is real and visible immediately.
  // `createdRequestId` is a blocker, not just a save-path guard: unlinking a
  // request brings this button back, and clicking it again would raise a second
  // Sell Request for the same consignment.
  const createRequestBlockedNote =
    createdRequestId
      ? `Sell Request #${createdRequestId} was already raised for this consignment. Unlinking it here does not delete it — reattach it from the listing's edit screen.`
      : !consignor?.id
        ? "Assign the consignor first — the Sell Request is raised in their name."
        : !product?.id
          ? "Pick the product first — a Sell Request cannot be created without one."
          : null;

  const createRequestNow = async () => {
    const created = await createDoneSellRequest({
      clientId: consignor.id,
      productId: product.id,
      isConsignment: true,
    });
    setPendingAttachId(created.id);
    setCreatedRequestId(created.id);
    // Re-read it so the slot shows the client and product the API resolved,
    // not just the ids we sent.
    try {
      setSelectedRequest(await fetchRequest(created.id));
    } catch (e) {
      setSelectedRequest(created);
    }
  };

  // ── Agreements ──────────────────────────────────────────────────────────────
  const loadAgreement = async (key) => {
    if (!selectedRequest?.id) return;
    const fetcher = key === "consignment" ? fetchConsignmentAgreement : fetchPurchasingAgreement;
    setAgreements((a) => ({ ...a, [key]: { loading: true } }));
    try {
      const { file_id, signed_url } = await fetcher(selectedRequest.id);
      setAgreements((a) => ({ ...a, [key]: { url: signed_url || file_id } }));
    } catch (e) {
      setAgreements((a) => ({ ...a, [key]: { error: `Could not fetch the ${key} agreement.` } }));
    }
  };

  // Auto-surface the linked request's documents (see useAutoContractDocs):
  // existing agreements fetch on link; the consignment agreement and the Xero
  // PO auto-generate when the request has a quote and they don't exist yet.
  const [poAutoNote, setPoAutoNote] = useState(null);
  useAutoContractDocs({
    request: selectedRequest,
    kind: "consignment",
    agreements,
    loadAgreement,
    poPresent: !!poUrl,
    onPo: ({ ok, data, message }) => {
      if (ok) {
        setPoAutoNote(null);
        setSelectedRequest((prev) =>
          prev
            ? {
                ...prev,
                xero_purchase_order_file_id: data.xero_purchase_order_file_id,
                xero_purchase_order_file_url: data.xero_purchase_order_file_url,
              }
            : prev
        );
      } else {
        setPoAutoNote(message);
      }
    },
  });

  // ── Prefill ─────────────────────────────────────────────────────────────────
  // A selected Sell Request carries the Confirm Request answers (condition,
  // delivery set, year) plus the cost/serial figures, so it wins over Gemini's
  // image reading. Owned By is SEEDED to "exclusive" — what makes this listing a
  // consignment — but stays the rep's to change: `enforce` writes page state into
  // Formik and `reportFields` reports Formik's live value back, so the intake
  // dropdown and the Listings-section field are one value. It has to go through
  // `enforce` rather than `prefill` because the form defaults internal_product to
  // "owned" for every new listing — the field is never empty, and a soft prefill
  // could never change it.
  // The assigned consignor IS the listing's supplier: `supplier`/`supplier_id`
  // are ENFORCED (not prefilled) so changing the consignor mid-flow updates
  // the listing, and `clear` empties both on an explicit unassign — the raw
  // Supplier picker this replaces was removed from Internal Details
  // 2026-09-04. supplier_id feeds trade routes, order export and PnL, so the
  // name must never travel without the id.
  const enforce = useMemo(
    () => ({
      internal_product: ownedBy,
      is_repair: isRepair,
      ...(consignor?.id ? { supplier: consignor.name, supplier_id: consignor.id } : {}),
    }),
    [ownedBy, isRepair, consignor?.id, consignor?.name]
  );

  const prefill = useMemo(() => ({
    ...listingPrefillFromRequest(selectedRequest),
    serial_number: selectedRequest?.serial_number ?? aiFields?.serial_number,
    warranty_date: aiFields?.warranty_date,
    year: selectedRequest?.year ?? aiFields?.year,
  }), [aiFields, selectedRequest]);

  // An explicit unassign has to empty both stored values — the only bucket
  // allowed to write an empty value. A never-assigned consignor is a no-op.
  const clear = consignor === null ? ["supplier", "supplier_id"] : undefined;

  // ── Save orchestration ──────────────────────────────────────────────────────
  // One rule for both halves of the Sell Request question — see
  // sellRequestActionOnSave. Read here (not only inside afterSubmit) because
  // CONTRACTS says whether a PO is still coming.
  const requestAction = sellRequestActionOnSave({ option, pendingAttachId, createdRequestId });

  const afterSubmit = async (saved) => {
    const savedId = saved?.id || saved?.listing?.id;
    const intent = intentRef.current;

    if (intent === "publish" && savedId) {
      try {
        await activateListing(savedId);
      } catch (e) {
        alert.error("Listing saved but publishing failed — activate it from the listing card.");
      }
    }

    // A request created earlier in the flow, or an existing one the rep chose,
    // only needs its link now — the listing did not exist when it was raised.
    if (requestAction === "attach" && savedId) {
      try {
        await attachListingToRequest(pendingAttachId, savedId);
      } catch (e) {
        alert.info(
          `Listing saved — Sell Request #${pendingAttachId} could not be linked to it automatically.`
        );
      }
    } else if (requestAction === "create" && consignor?.id && product?.id && savedId) {
      // The walk-in route without visiting Internal Details: raise the
      // consignor's terminal Sell Request now, already linked.
      try {
        await createDoneSellRequest({
          clientId: consignor.id,
          productId: product.id,
          listingId: savedId,
          isConsignment: true,
        });
      } catch (e) {
        alert.info("Listing saved — the linked Sell Request could not be created automatically.");
      }
    } else if (requestAction === "create" && savedId) {
      alert.info(
        "Listing saved — no Sell Request was created: it needs both a consignor and a product."
      );
    }

    if (savedId) history.push(`/product/${savedId}${iframeQueryParams ? `?${iframeQueryParams}` : ""}`);
  };

  // ── Sections ────────────────────────────────────────────────────────────────
  const topSections = (
    <FlowSection
      icon={<IUserIcon />}
      label="Assign Consignor"
      isOpen={openSection === "consignor"}
      onToggle={() => toggleSection("consignor")}
      summary={
        [
          option ? OPTIONS.find((o) => o.key === option)?.label : null,
          consignor?.name,
          option === "existing" && selectedRequest ? `#${selectedRequest.id}` : null,
          // In Repair is set here but read much later (the card, the Cost tab),
          // so it has to survive the collapse — otherwise the only way to check
          // it is to reopen the section on spec.
          isRepair ? "In repair" : null,
        ]
          .filter(Boolean)
          .join(" · ") || null
      }
    >
      {/* Sized from the option count, so merging or adding a route can never
          leave the row half-empty the way dropping to two options did. */}
      <RadioGroup
        layout="cards"
        legend="Consignment route"
        hideLegend
        value={option}
        onChange={(key) => pickOption(key)}
      >
        {OPTIONS.map((o) => (
          <Radio key={o.key} value={o.key} label={o.label} description={o.hint} />
        ))}
      </RadioGroup>

      {option === "walkin" && (
        <div className="rc-flow__walkin">
          {/* Both pictures are optional — page state, not Formik, and nothing
              in the save path reads them. They only feed the image
              recognition, so a rep who already knows the watch skips them. */}
          <div className="rc-flow__source-grid rc-flow__source-grid--tight">
            <IntakeFileBox
              label="Picture of the watch (optional)"
              accept="image/*"
              file={watchImage}
              uploading={uploadingSlot === "watch"}
              onPick={(f) => onImagePick("watch", f)}
              onClear={() => setWatchImage(null)}
              hint="Read for the product, reference and serial."
            />
            <IntakeFileBox
              label="Warranty card (optional)"
              accept="image/*"
              file={warrantyImage}
              uploading={uploadingSlot === "warranty"}
              onPick={(f) => onImagePick("warranty", f)}
              onClear={() => setWarrantyImage(null)}
              hint="Read for the warranty date."
            />
          </div>
          <AiStatus state={aiState} empty={aiEmpty} />
          {aiState === "done" && aiFields && (
            <div className="rc-flow__hint">
              {[
                aiFields.serial_number ? `Serial ${aiFields.serial_number}` : null,
                aiFields.warranty_date ? `Warranty ${aiFields.warranty_date}` : null,
              ].filter(Boolean).join(" · ") || "No serial/warranty details detected."}
              {" "}Applied to Internal Details.
            </div>
          )}
        </div>
      )}

      {option === "existing" && (
        <div className="rc-flow__existing">
          <SellRequestSearch
            selected={selectedRequest}
            onSelect={onRequestSelect}
            onClear={() => {
              setSelectedRequest(null);
              setAgreements({});
              setSuggestions([]);
              // Dropping the selection drops the link with it, back to whatever
              // this flow raised itself (usually nothing on this route).
              setPendingAttachId(createdRequestId);
            }}
          />
          {selectedRequest && poUrl && (
            <div className="rc-lp__detail-pdf rc-flow__po-preview">
              <PdfShower fileId={poUrl} />
            </div>
          )}
        </div>
      )}

      {option && (
        <AssignPersonPicker
          label="Consignor"
          suggestions={suggestions}
          suggestionsNote={
            suggestions.length > 0
              ? (option === "existing" ? "Picked up from the selected Sell Request." : "Suggested from the uploaded images.")
              // The pictures are optional, so on the walk-in route there is
              // usually nothing to suggest from — say what to do instead of
              // leaving an unexplained gap above the search box.
              : option === "existing"
                ? null
                : "Search for the consignor below, or add them as a new user."
          }
          value={consignor}
          onChange={setConsignor}
          createTitle="New Consignor"
        />
      )}

      {/* Last things in the section, in this order deliberately: the rep sets
          Owned By and In Repair and then commits. Continue is the only thing
          that collapses the section, so both are always reachable first. Owned
          By is the SAME field as Listings ▸ Owned By, not a copy of it — see
          `enforce` / `reportFields`. */}
      <OwnedByField
        value={ownedBy}
        onChange={setOwnedBy}
        storeName={settingsCtx?.settings?.name}
      />
      <RepairToggleField value={isRepair} onChange={setIsRepair} />

      {intakeSatisfied && (
        <div className="rc-flow__source-actions">
          <ButtonGroup align="end">
            <Button data-btn="consignor-confirm" onClick={continueFromConsignor}>
              Continue
            </Button>
          </ButtonGroup>
        </div>
      )}
    </FlowSection>
  );

  const agreementSlots = [
    {
      key: "purchasing",
      label: "Purchasing Agreement",
      url: agreements.purchasing?.url,
      loading: agreements.purchasing?.loading,
      error: agreements.purchasing?.error,
      onLoad: selectedRequest?.id ? () => loadAgreement("purchasing") : null,
    },
    {
      key: "consignment",
      label: "Consignment Agreement",
      url: agreements.consignment?.url,
      loading: agreements.consignment?.loading,
      error: agreements.consignment?.error,
      onLoad: selectedRequest?.id ? () => loadAgreement("consignment") : null,
    },
  ];

  // Internal Details shows the linked Sell Request instead of a raw Supplier
  // picker. Selecting one here is equivalent to the Existing Request option;
  // Create raises the walk-in route's request early rather than on save.
  const supplierSlot = (
    <LinkedRequestSlot
      selected={selectedRequest}
      onSelect={(req) => { pickOption("existing"); onRequestSelect(req); }}
      onClear={() => {
        setSelectedRequest(null);
        setAgreements({});
        setSuggestions([]);
        // Only the LINK is dropped here. A request created a moment ago is left
        // alone rather than deleted — it may already carry paperwork, and the
        // rep can attach it again from the listing's edit screen. It still
        // counts as raised: saving now must not create a second one.
        setPendingAttachId(null);
      }}
      onCreate={createRequestNow}
      createBlockedNote={createRequestBlockedNote}
    />
  );

  const flow = useMemo(() => ({
    title: "New Consignment",
    product,
    // The selector's own choice, so "Create Sell Request" has a product_id even
    // when nothing was auto-picked. Guarded: the form also reports the product
    // WE pushed in, and re-setting it here would loop.
    onProductChange: (picked) => {
      if ((picked?.id ?? null) !== (product?.id ?? null)) setProduct(picked);
    },
    prefill,
    enforce,
    clear,
    // Mirror the Cost-tab In Repair toggle and the Listings-section Owned By
    // dropdown back out to page state, so the ASSIGN CONSIGNOR copies reflect a
    // change made there. enforce carries the other direction; both write the
    // same value, so no loop.
    reportFields: ["is_repair", "internal_product"],
    onReport: (v) => {
      setIsRepair(!!v.is_repair);
      if (v.internal_product) setOwnedBy(v.internal_product);
    },
    topSections,
    supplierSlot,
    openSectionRequest: formSectionRequest,
    openProductRequest: productRequest,
    renderContractsSection: ({ openSection: formOpen, toggleSection: formToggle }) => (
      <ContractsSection
        openSection={formOpen}
        toggleSection={formToggle}
        poUrl={poUrl}
        poEmptyNote={
          poAutoNote ??
          (requestAction === "create"
            ? "The PO generated for the Sell Request raised on save will sync here once backend linking ships — until then, generate it from the request flow."
            : undefined)
        }
        agreementSlots={agreementSlots}
      />
    ),
    renderStatusSection: ({ submitForm }) => (
      <ListingStatusSection submitForm={submitForm} intentRef={intentRef} publishLabel="Publish Consignment" />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [product, prefill, enforce, clear, topSections, supplierSlot, formSectionRequest, productRequest, poUrl, poAutoNote, agreements, selectedRequest, option, isRepair, ownedBy, settingsCtx, requestAction]);

  return (
    <>
      <Helmet>
        <title>New Consignment — Retail Circle</title>
        <meta name="robots" content="noindex, nofollow, noarchive" />
      </Helmet>
      <RCListingForm listing={{}} afterSubmit={afterSubmit} flow={flow} />
    </>
  );
};

export default RCConsignmentListingCreate;
